import { useCallback, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { toast } from "sonner";
import { copyElementAsImage, OFFER_FONT_SIZE_MAX } from "@/lib/offer-export";
import { mergeOfferWritingForExport } from "@/lib/offer-calc";
import { isLineVisible } from "@/lib/offer-budget-image-geometry";
import type { BudgetPageSlice } from "@/lib/offer-export-paginate";
import type { OfferLine } from "@/hooks/use-offers";
import type { OfferSection } from "@/hooks/use-offer-sections";

interface UseOfferBudgetImageCopyArgs {
  sections: OfferSection[];
  lines: OfferLine[];
  fileBaseName?: string;
}

interface UseOfferBudgetImageCopyResult {
  budgetImageRef: React.RefObject<HTMLDivElement>;
  budgetFontSize: number;
  budgetTwoColumns: boolean;
  budgetPageSlice: BudgetPageSlice | null;
  copyingSectionKey: string | null | undefined;
  handleCopySectionImage: (sectionKey: string | null) => Promise<void>;
}

function linesForSection(
  lines: OfferLine[],
  sectionKey: string | null,
): OfferLine[] {
  const visible = mergeOfferWritingForExport(lines).filter(isLineVisible);
  if (typeof sectionKey === "string") {
    return visible.filter((l) => l.section_id === sectionKey);
  }
  return visible.filter((l) => !l.section_id);
}

function safeFileBase(label: string): string {
  return label.replace(/[^a-zA-Z0-9_-]+/g, "_") || "Budsjett";
}

export function useOfferBudgetImageCopy({
  sections,
  lines,
  fileBaseName = "Budsjett",
}: UseOfferBudgetImageCopyArgs): UseOfferBudgetImageCopyResult {
  const budgetImageRef = useRef<HTMLDivElement>(null);
  const [budgetFontSize, setBudgetFontSize] = useState<number>(OFFER_FONT_SIZE_MAX);
  const [budgetTwoColumns, setBudgetTwoColumns] = useState(false);
  const [budgetPageSlice, setBudgetPageSlice] =
    useState<BudgetPageSlice | null>(null);
  const [copyingSectionKey, setCopyingSectionKey] = useState<
    string | null | undefined
  >(undefined);

  const handleCopySectionImage = useCallback(
    async (sectionKey: string | null) => {
      if (copyingSectionKey !== undefined) return;
      setCopyingSectionKey(sectionKey);
      try {
        await new Promise<void>((resolve) =>
          requestAnimationFrame(() =>
            requestAnimationFrame(() => resolve()),
          ),
        );
        if (!budgetImageRef.current) {
          toast.error("Budsjett-bilde ikke klart enda");
          return;
        }
        const label =
          sectionKey === null
            ? "Hovedbudsjett"
            : sections.find((s) => s.id === sectionKey)?.name ?? "Tilvalg";
        const { pageCount } = await copyElementAsImage(
          budgetImageRef.current,
          setBudgetFontSize,
          setBudgetTwoColumns,
          {
            visibleLines: linesForSection(lines, sectionKey),
            setPageSlice: (slice) => {
              flushSync(() => {
                setBudgetPageSlice(slice);
              });
            },
            downloadBaseName: safeFileBase(fileBaseName),
          },
        );
        setBudgetFontSize(OFFER_FONT_SIZE_MAX);
        setBudgetTwoColumns(false);
        setBudgetPageSlice(null);
        if (pageCount > 1) {
          toast.success(
            `${label} ble for stort for ett bilde. ${pageCount} sider er lastet ned — ingenting er kopiert.`,
          );
        } else {
          toast.success(`${label} kopiert som bilde`);
        }
      } catch (err) {
        toast.error(`Kunne ikke kopiere: ${(err as Error).message}`);
      } finally {
        setCopyingSectionKey(undefined);
      }
    },
    [copyingSectionKey, sections, lines, fileBaseName],
  );

  return {
    budgetImageRef,
    budgetFontSize,
    budgetTwoColumns,
    budgetPageSlice,
    copyingSectionKey,
    handleCopySectionImage,
  };
}
